import {
  type AudienceSegmentEntry,
  type AudienceSegmentSet,
  type NormalizedDefinition,
  type NormalizedDefinitionGroup,
  NormalizedDefinitionOperator,
  type NormalizedDefinitionSegment,
  type NormalizedDefinitionSegmentGroup,
} from '@openx/types/gqlTypes';
import type { NormalizeDefinitionType, SegmentGroupCategory, SegmentGroupItem } from './types';

const CATEGORY_KEYS_ON_GROUP: Array<
  keyof Pick<
    NormalizedDefinitionGroup,
    'direct' | 'location' | 'lookalike' | 'predefined' | 'demographic_age' | 'demographic_gender'
  >
> = ['direct', 'location', 'lookalike', 'predefined', 'demographic_age', 'demographic_gender'];

const SELECTABLE_CATEGORY_KEYS: Array<keyof Pick<NormalizedDefinitionGroup, 'direct' | 'predefined'>> = [
  'direct',
  'predefined',
];

function normalizedDefinitionGroupHasCategoryContent(
  group: Pick<NormalizedDefinitionGroup, (typeof CATEGORY_KEYS_ON_GROUP)[number]>
): boolean {
  return CATEGORY_KEYS_ON_GROUP.some(key => group[key] != null && (group[key]!.segments?.length ?? 0) > 0);
}

/**
 * Returns a copy of the normalized definition ready for network payloads (pricing query, audience insert):
 * - removes `matched_keywords` from each group
 * - omits category properties (direct, location, lookalike, etc.) that are null or have no segments
 * - drops groups that would contain only `operator` (no category segment data)
 */
export function sanitizeNormalizedDefinitionForPricingQuery(
  definition: NormalizeDefinitionType
): NormalizeDefinitionType {
  const groups = definition.includes.groups
    .map(group => {
      const cleaned: Omit<NormalizedDefinitionGroup, 'matched_keywords'> = {
        operator: group.operator,
      };

      if (group.region != null) {
        cleaned.region = group.region;
      }

      if (group.countries?.length) {
        cleaned.countries = group.countries;
      }

      for (const key of CATEGORY_KEYS_ON_GROUP) {
        const value = group[key];

        if (value?.segments?.length) {
          cleaned[key] = value;
        }
      }

      return cleaned;
    })
    .filter(normalizedDefinitionGroupHasCategoryContent);

  return {
    ...definition,
    includes: {
      ...definition.includes,
      groups,
    },
  };
}

/**
 * Recursively strips `__typename` from an object (and nested objects/arrays).
 * Returns a new object; does not mutate the input.
 */
export function stripTypename<T>(value: T): T {
  if (value === null || typeof value !== 'object') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(stripTypename) as T;
  }

  const result = { ...value } as Record<string, unknown>;

  delete result.__typename;

  for (const key of Object.keys(result)) {
    result[key] = stripTypename(result[key]);
  }

  return result as T;
}

/**
 * Builds a normalized definition for pricing by taking the base from the given
 * segment set's normalized_definition (from GetAudienceSegmentSearchText, e.g. the
 * set from selectedTile.item), stripping __typename, and restricting segments
 * to those in selectedAudienceItems.
 * When segmentSet is null or has no normalized_definition, builds the definition
 * from selectedAudienceItems via mapSegmentsToIncludeGroups.
 */
type DefinitionGroupWithoutMatchedKeywords = Omit<NormalizedDefinitionGroup, 'matched_keywords'>;

function groupsToNormalizedDefinition(groups: DefinitionGroupWithoutMatchedKeywords[]): NormalizeDefinitionType {
  const operator = NormalizedDefinitionOperator.Intersect;

  return { includes: { operator, groups } };
}

export function createNormalizedDefinitionFromSegmentSetAndSelection(
  segmentSet: AudienceSegmentSet | null,
  selectedAudienceItems: AudienceSegmentEntry[]
): NormalizeDefinitionType {
  const selectedIds = new Set(selectedAudienceItems.map(s => s.id));
  const rawBase = segmentSet?.normalized_definition;

  if (rawBase == null) {
    const groups = mapSegmentsToIncludeGroups(selectedAudienceItems);
    return groupsToNormalizedDefinition(groups);
  }

  const base = stripTypename(JSON.parse(JSON.stringify(rawBase)) as NormalizedDefinition);

  for (const group of base.includes.groups) {
    for (const key of SELECTABLE_CATEGORY_KEYS) {
      const segmentGroup = group[key] as NormalizedDefinitionSegmentGroup | null | undefined;

      if (!segmentGroup?.segments) continue;

      const filtered = segmentGroup.segments.filter(seg => selectedIds.has(seg.id));

      (group as Record<string, unknown>)[key] =
        filtered.length === 0 ? undefined : { ...segmentGroup, segments: filtered };
    }

    delete (group as Record<string, unknown>).matched_keywords;
  }

  return base;
}

const GROUP_CATEGORY_KEYS: SegmentGroupCategory[] = [
  'direct',
  'location',
  'lookalike',
  'predefined',
  'demographic_age',
  'demographic_gender',
];

const DEFAULT_CATEGORY: SegmentGroupCategory = 'direct';

/**
 * Partitions segments by their category and builds a single group with each
 * category property set to a segment group (segments + UNION operator).
 * Only categories that have at least one segment are set.
 * Segments without a category are treated as 'direct'.
 */
export function mapSegmentsToIncludeGroups(segments: AudienceSegmentEntry[]): DefinitionGroupWithoutMatchedKeywords[] {
  const byCategory = GROUP_CATEGORY_KEYS.reduce<Partial<Record<SegmentGroupCategory, NormalizedDefinitionSegment[]>>>(
    (acc, key) => {
      const list = segments.filter(s => (s.category ?? DEFAULT_CATEGORY) === key);

      if (list.length > 0) acc[key] = list.map(s => ({ id: s.id }));

      return acc;
    },
    {}
  );

  const segmentGroup = (list: NormalizedDefinitionSegment[]): SegmentGroupItem<NormalizedDefinitionSegment> => ({
    segments: list,
    operator: NormalizedDefinitionOperator.Union,
  });

  const group: DefinitionGroupWithoutMatchedKeywords = {
    operator: NormalizedDefinitionOperator.Intersect,
    ...(byCategory.direct && { direct: segmentGroup(byCategory.direct) }),
    ...(byCategory.location && { location: segmentGroup(byCategory.location) }),
    ...(byCategory.lookalike && { lookalike: segmentGroup(byCategory.lookalike) }),
    ...(byCategory.predefined && { predefined: segmentGroup(byCategory.predefined) }),
    ...(byCategory.demographic_age && { demographic_age: segmentGroup(byCategory.demographic_age) }),
    ...(byCategory.demographic_gender && { demographic_gender: segmentGroup(byCategory.demographic_gender) }),
  };

  return [group];
}
